import { faThumbsUp } from '@fortawesome/pro-solid-svg-icons';
import { ActionButton, HeadingItem } from 'components';
import { BarcodeReader } from 'components/barcode';
import { AppointmentCard } from 'components/load-appointments';
import { ConfirmationPopup } from 'components/pop-up';
import { TripContext } from 'contexts';
import { Popup } from 'devextreme-react/popup';
import { ScrollView } from 'devextreme-react/scroll-view';
import { ItemVerificationView, ScanResultType, ScanResultValueType } from 'enums';
import React, { FC, Fragment, ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import soundFile from 'resources/unknownBarcodeSound.mp3';
import { useLicensePlateService, useNavigation, useOrderItemService, useReturnItemService } from 'services';
import { OrderType, VerifiedMethod } from 'services/enums';
import { FilteredLicensePlate, LicensePlate, LineItem, LineItemBarcode, Order, OrderItem, ReturnItem, Stop } from 'services/types';
import { Barcode, ScanResult, ScanResultValue } from 'types';
import './barcode-search.scss';

type Props = {
    tripId: number | null | undefined
    stopId: number | null | undefined
    onLpScanned?: (result: ScanResult) => void
    onItemScanned?: (result: ScanResult) => void
    additionalInvalidBarcodeCriteria?: (result: ScanResult) => boolean
}

export const BarcodeSearch: FC<Props> = ({ tripId, stopId, onLpScanned, onItemScanned, additionalInvalidBarcodeCriteria}) => {

    const audio = useMemo(() => new Audio(soundFile), []);
    const { verifyItems } = useOrderItemService()
    const { verifyLp, areAllLpItemsVerified } = useLicensePlateService()
    const { verifyReturnItem } = useReturnItemService()
    const { navigateTo } = useNavigation()
    const [scanResult, setScanResult] = useState<ScanResult>()
    const isLpResult: boolean = [ScanResultType.LPCurrentStop, ScanResultType.LPDifferentStop, ScanResultType.LPDifferentLoad].includes(scanResult?.type ?? ScanResultType.BarcodeNotFound)

    const tripContext = useContext(TripContext)

    //Convert the scanned value to an appropriate UPC
    const convertToUpc = (scannedCode: string): string => {
        let trimmedBarcode: string = scannedCode.replace(/\D/g, '').trim()
        let upc: string = trimmedBarcode.padStart(14, '0')
        if (trimmedBarcode.length > 14) {
            upc = trimmedBarcode.substring(2, 16);
        }
        return upc
    }

    const vibrateDevice = async (orderItems: OrderItem[] | null = null, lp: LicensePlate | null = null) => {
        let notVerifiedOrderItems = orderItems && orderItems.filter(item => item.verifiedMethod == null)
        if ((notVerifiedOrderItems && notVerifiedOrderItems.length === 0) || (lp && lp.verifiedMethod != null)) {
            navigator.vibrate([200, 100, 200, 100, 200, 100]) // Vibrate for 200ms and pause for 100ms which repeats 3 times
        }
    }

    useEffect(() => {
        const handleScanResult = async () => {

            if (scanResult && scanResult.values && scanResult.values.length > 0) {
                switch (scanResult.type) {
                    case ScanResultType.LPCurrentStop:
                        const isStcStop = tripContext.stops.find(s => s.id === stopId)?.orderTypes?.some(item => item == OrderType.SubjectToCount)
                        const licensePlate: LicensePlate = scanResult.values[0].value as LicensePlate
                        let entry: FilteredLicensePlate = { id: licensePlate.id, stopId: licensePlate.stopId }

                        vibrateDevice(null, licensePlate)
                        if (onLpScanned) {
                            onLpScanned(scanResult)
                        }
                        else {
                            tripContext.filteredLicensePlates.put(entry)
                            verifyLp(licensePlate.id, VerifiedMethod.Scanned)

                            if (isStcStop)
                                await navigateTo(`lp-details?lpId=${licensePlate.id}`)
                            else
                                await navigateTo(`item-verification?stopId=${stopId}&searchId=${ItemVerificationView.NotVerified}&dry=${true}&frozen=${true}&ref=${true}`)

                        }
                        break;
                    case ScanResultType.LineItemCurrentStop:
                    case ScanResultType.MultipleScannedValues:
                        const scanValues: ScanResultValue[] = scanResult.values.filter(value => value.type == ScanResultValueType.OrderItem) ?? []
                        const items: OrderItem[] = scanValues.map(item => { return item.value as OrderItem })

                        if (onLpScanned == null) {
                            vibrateDevice(items)
                        }

                        if (onItemScanned) {
                            onItemScanned(scanResult)
                        }
                        else {
                            const orders: Order[] = tripContext.orders.toArray() ?? []
                            const stopLps: LicensePlate[] = tripContext.licensePlates.filter(lp => lp.stopId == stopId) ?? []

                            if (areAllLpItemsVerified(stopLps, items[0].lineItemId)) {
                                verifyItems(items, VerifiedMethod.Scanned)
                            }

                            let order: Order | undefined = orders.find(o => o.id === items[0].orderId)
                            if (order?.signatureId == null) {
                                await navigateTo(`verify-item?stopId=${stopId}&itemId=${items[0].lineItemId}&verifiedMethod=${VerifiedMethod.Scanned}`)
                            } else {
                                await navigateTo(`item-details?orderItemId=${items[0].id}`)
                            }
                        }
                        break;
                    case ScanResultType.ReturnItemCurrentStop:
                        if (onItemScanned) {
                            onItemScanned(scanResult)
                        }
                        else {
                            const scanValue: ScanResultValue = scanResult.values.find(value => value.type == ScanResultValueType.ReturnItem) ?? {} as ScanResultValue
                            const item: ReturnItem = scanValue.value as ReturnItem
                            await verifyReturnItem(item, VerifiedMethod.Scanned)
                            await navigateTo(`verify-returnitem?stopId=${stopId}&returnItemId=${item.id}`)
                        }
                        break;
                    case ScanResultType.BarcodeNotFound:
                        if (onLpScanned == null) {
                            audio.play()
                        }
                        onItemScanned && onItemScanned(scanResult)
                        break;
                    case ScanResultType.LPDifferentStop:
                        audio.play()
                        onItemScanned && onItemScanned(scanResult)
                        break;
                    case ScanResultType.LineItemDifferentStop:
                        audio.play()
                        onItemScanned && onItemScanned(scanResult)
                        break;
                    default:
                        if (onItemScanned)
                            onItemScanned(scanResult)
                        else if (onLpScanned)
                            onLpScanned(scanResult)
                        break;
                }
            }
        }

        handleScanResult()
    }, [scanResult])

    const handleBarcodeSearch = async (scannedBarcode: string): Promise<void> => {
        let barcode: Barcode = {} as Barcode
        barcode.raw = scannedBarcode.trim()
        barcode.processed = convertToUpc(scannedBarcode)

        if (tripId && stopId) {

            const appointments: Stop[] = tripContext.stops.filter(s => s.tripId === tripId)
            let lineItem: LineItem | undefined = tripContext.lineItems.find(li => li.universalProductCode === barcode.processed)
            if (lineItem == null) {
                const lineItemBarcodes: LineItemBarcode[] | undefined = tripContext.lineItemBarcodes.filter(lib => lib.barcode === barcode.processed || lib.barcode === barcode.raw)
                if (lineItemBarcodes != null && lineItemBarcodes.length > 0) {
                    const lineItemBarcode: LineItemBarcode = lineItemBarcodes.sort((a,b) => {return a.priority - b.priority})[0]
                    lineItem = tripContext.lineItems.find(item => item.id === lineItemBarcode.lineItemId)
                }
            }
            const licensePlate = tripContext.licensePlates.find(li => li.id === parseInt(scannedBarcode))

            let lpOrderItems: OrderItem[] = []
            if (lineItem != null) {
                lpOrderItems = tripContext.orderItems.filter(oi => oi.lineItemId == lineItem?.id)
            }

            let result: ScanResult | undefined = { type: ScanResultType.BarcodeNotFound, values: [] }
            if (lineItem) {
                const stopOrders = tripContext.orders.filter(o => o.stopId === stopId)
                const otherStopOrders = tripContext.orders.filter(o => o.stopId !== stopId)
                const appointmentOrders = tripContext.orders.filter(o => appointments.some(a => a.id === o.stopId))
                const stopOrderItems: OrderItem[] = lpOrderItems.filter(items => stopOrders.some(o => o.id === items.orderId))
                const stopReturnItem: ReturnItem | undefined = tripContext.returnItems.find(item => stopOrders.some(o => o.id === item.orderId) && item.lineItemId === lineItem?.id)
                let otherStopItems: OrderItem[] = lpOrderItems.filter(items => otherStopOrders.some(o => o.id === items.orderId))
                otherStopItems = otherStopItems.filter(items => appointmentOrders.some(ao => ao.id === items.orderId))

                if (stopOrderItems && stopOrderItems.length > 0 && stopReturnItem) {
                    const scanResultValues: ScanResultValue[] = stopOrderItems.map(item => { return { type: ScanResultValueType.OrderItem, value: item } as ScanResultValue })
                    const returnItemResult: ScanResultValue = { type: ScanResultValueType.ReturnItem, value: stopReturnItem }
                    scanResultValues.push(returnItemResult)
                    result = { type: ScanResultType.MultipleScannedValues, values: scanResultValues }
                }
                else if (stopOrderItems && stopOrderItems.length > 0) {
                    const orderItemResult: ScanResultValue[] = stopOrderItems.map(item => { return { type: ScanResultValueType.OrderItem, value: item } as ScanResultValue })
                    result = { type: ScanResultType.LineItemCurrentStop, values: orderItemResult }
                } else if (stopReturnItem) {
                    const returnItemResult: ScanResultValue = { type: ScanResultValueType.ReturnItem, value: stopReturnItem }
                    result = { type: ScanResultType.ReturnItemCurrentStop, values: [returnItemResult] }
                }
                else if (otherStopItems && otherStopItems.length > 0) {
                    const otherStopItemResult: ScanResultValue[] = otherStopItems.map(item => { return { type: ScanResultValueType.OrderItem, value: item } as ScanResultValue })
                    //  line item belongs to another stop - show pop-up
                    result = { type: ScanResultType.LineItemDifferentStop, values: otherStopItemResult }
                }
            }
            else if (licensePlate) {
                const licensePlateResult: ScanResultValue = { type: ScanResultValueType.LicensePlate, value: licensePlate }
                if (licensePlate.stopId === stopId) {
                    result = { type: ScanResultType.LPCurrentStop, values: [licensePlateResult] }
                } else if (appointments.some(appt => appt.id === licensePlate.stopId)) {
                    //LP belongs to another stop - show pop-up
                    result = { type: ScanResultType.LPDifferentStop, values: [licensePlateResult] }
                } 
            }
            else {
                const barcodeNotFoundResult: ScanResultValue = { type: ScanResultValueType.BarcodeNotFound, value: scannedBarcode }
                //Show the pop-up , scanned item / LP does not belong to any of the stop
                result = { type: ScanResultType.BarcodeNotFound, values: [barcodeNotFoundResult] }
                if(additionalInvalidBarcodeCriteria != null) {
                    const meetsAdditionalRequirements: boolean = additionalInvalidBarcodeCriteria(result)
                    if(!meetsAdditionalRequirements) {
                        result = undefined
                    }
                }
            }
            setScanResult(result)
        }
    }

    const renderAppointments = (): ReactNode => {
        if (scanResult && scanResult.values && scanResult.values.length > 0) {
            if (isLpResult) {
                const scanValue: ScanResultValue = scanResult.values.find(value => value.type == ScanResultValueType.LicensePlate) ?? {} as ScanResultValue
                const lp: LicensePlate = (scanValue.value as LicensePlate) ?? {} as LicensePlate
                const lpStop = tripContext.stops.find(s => s.id === lp.stopId)
                return <AppointmentCard key={lpStop?.id} stopId={lpStop?.id} isReadOnly={true} />
            }
            else {
                if (scanResult?.type === ScanResultType.ReturnItemCurrentStop) {
                    const scanValue: ScanResultValue = scanResult.values.find(value => value.type == ScanResultValueType.ReturnItem) ?? {} as ScanResultValue
                    const item: ReturnItem = (scanValue.value as ReturnItem)
                    const stopId = tripContext.orders.find(o => o.id === item.orderId)?.stopId;
                    return stopId ? <AppointmentCard stopId={stopId} isReadOnly={true} />
                        : <Fragment></Fragment>
                } else {
                    const scanValues: ScanResultValue[] = scanResult.values.filter(value => value.type == ScanResultValueType.OrderItem) ?? []
                    const items: OrderItem[] = scanValues.map(item => { return item.value as OrderItem })
                    return <Fragment>
                        {items.map(result => {
                            const stopId = tripContext.orders.find(o => o.id === result.orderId)?.stopId;
                            return stopId ? <AppointmentCard key={result.id} stopId={stopId} isReadOnly={true} />
                                : <Fragment></Fragment>
                        })
                        }
                    </Fragment>
                }
            }
        }
    }

    return (
        <div className={'barcode-search-component'}>
            <Fragment>
                <BarcodeReader onScan={(barcode) => handleBarcodeSearch(barcode)} />
                <Popup
                    title={`Warning`}
                    visible={(scanResult?.type === ScanResultType.LPDifferentStop || scanResult?.type === ScanResultType.LineItemDifferentStop) && onLpScanned == null && onItemScanned == null}
                    width={'90vw'}
                    height={scanResult?.type !== ScanResultType.BarcodeNotFound ? '80vh' : '25vh'}
                    maxWidth={400}
                    showTitle={false}
                >
                    <HeadingItem text={'Warning'} />
                    <div className={'barcode-search-different-stop-message'}>{`This ${isLpResult ? 'LP' : 'item'} is on a different stop`}</div>
                    <ScrollView style={{ paddingBottom: '7em' }}>
                        <div className={'barcode-search-different-stop-stops'}>
                            {scanResult?.type !== ScanResultType.BarcodeNotFound && renderAppointments()}
                        </div>
                    </ScrollView>
                    <div className={'bottom-fix-action-row'}>
                        <ActionButton
                            icon={faThumbsUp}
                            buttonColor={'#525252'}
                            onClick={() => setScanResult(undefined)}
                            buttonText={'OK'}
                        />
                    </div>
                </Popup>
                <ConfirmationPopup
                    isVisible={scanResult?.type === ScanResultType.BarcodeNotFound && onLpScanned == null && onItemScanned == null}
                    title="Error"
                    message={`The scanned barcode, ${scanResult?.values && scanResult?.values.length > 0 && scanResult?.values[0].value}, could not be found`}
                    button1={{ icon: faThumbsUp, text: 'OK' }}
                    isCloseButtonVisible={false}
                    onClickButton1={() => { audio.pause(); setScanResult(undefined); }}
                />
            </Fragment>
        </div>
    )
}